import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { MediaBlockService } from '../../services/media-block-service';
import { injectElementSize } from '../../utils/inject-element-size';
import { VideoCaptureFrame } from '../../services/video-capture-frame';
import { SkeletonComponent } from '@kinomap-dev/ng-ui-lib/basics';

@Component({
  selector: 'app-syncro-editor-video-visualizer',
  imports: [SkeletonComponent],
  template: `
    @if (!mediaBlockService.hasVideo()) {
      <p class="placeholder">Ajoutez une vidéo</p>
    }

    <div
      class="preview-wrapper"
      [class.generating]="isGenerating()"
      [style.width.%]="previewSizePercent()"
      [style.marginLeft.%]="syncOffsetPercent()"
    >
      @for (previewImageUrl of previewImagesUrls(); track $index) {
        <img [src]="previewImageUrl" alt="" />
      }
      @if (isGenerating()) {
        <kin-skeleton
          class="frames-skeleton"
          [extended]="true"
          [height]="40"
          unit="px"
          [borderRadius]="0"
        />
      }
    </div>
  `,
  styleUrl: './syncro-editor-video-visualizer.scss',
  host: {
    class: 'syncro-editor-video-visualizer',
    '[style.background]':
      "mediaBlockService.hasVideo() && !isGenerating() ? 'var(--text-color)' : 'var(--brand-secondary-100)'",
  },
})
export class SyncroEditorVideoVisualizer {
  mediaBlockService: MediaBlockService = inject(MediaBlockService);
  private readonly videoCaptureFrame = inject(VideoCaptureFrame);
  videoFile: InputSignal<File | undefined> = input.required();
  previewImagesUrls: WritableSignal<(string | null)[]> = signal<(string | null)[]>([]);
  isGenerating: WritableSignal<boolean> = signal<boolean>(false);
  private generationId: number = 0;
  size: Signal<DOMRectReadOnly | null> = injectElementSize();
  duration: Signal<number | undefined> = computed(
    () => this.mediaBlockService.videoFileData()?.duration,
  );
  cumuledTime: Signal<number | undefined> = this.mediaBlockService.cumuledTime;
  segmentCount: Signal<number> = computed(() =>
    Math.max(1, Math.floor((this.size()?.width ?? 0) / 120)),
  );

  timers: Signal<number[]> = computed(() => {
    const duration = this.duration();
    if (!duration) return [];
    const segmentCount = this.segmentCount();
    const segmentDuration = duration / segmentCount;
    const timers: number[] = [];
    for (let i = 0; i < segmentCount; i++) {
      timers.push(segmentDuration * i);
    }
    return timers;
  });

  previewSizePercent: Signal<number> = computed(() => {
    return ((this.duration() ?? 0) * 100) / (this.mediaBlockService.effectiveTotalDuration() ?? 0);
  });

  syncOffsetPercent: Signal<number> = computed(() => {
    const offset = this.mediaBlockService.syncOffsetSeconds();
    const effectiveTotal: number = this.mediaBlockService.effectiveTotalDuration() ?? 0;
    if (!effectiveTotal || !offset) return 0;
    return (offset * 100) / effectiveTotal;
  });

  constructor() {
    effect(() => {
      const timers = this.timers();
      const generation = ++this.generationId;

      // Pré-rempli depuis le cache de façon synchrone : pas de flash visuel
      // si les frames sont déjà disponibles (ex : réouverture de la modale fullscreen)
      const initialUrls: (string | null)[] = timers.map((t) => {
        const cached = this.videoCaptureFrame.getCached(t);
        return cached !== undefined ? cached : null;
      });
      this.previewImagesUrls.set(initialUrls);

      // Si tout est en cache, rien à faire
      const needsGeneration = timers.some((t) => this.videoCaptureFrame.getCached(t) === undefined);
      if (!needsGeneration) {
        this.isGenerating.set(false);
        return;
      }
      this.isGenerating.set(true);

      // Génère uniquement les frames absentes du cache
      (async () => {
        for (let i = 0; i < timers.length; i++) {
          if (generation !== this.generationId) return;
          if (this.videoCaptureFrame.getCached(timers[i]) !== undefined) continue;

          const url = await this.videoCaptureFrame.captureFrame(timers[i]);
          if (generation !== this.generationId) return;

          const current = this.previewImagesUrls();
          current[i] = url;
          this.previewImagesUrls.set([...current]);
        }
        this.isGenerating.set(false);
      })();
    });
  }
}
